// composables/useAuth.ts
// Adapté de src/auth/useAuth.js (Vue3+Vite) pour Nuxt :
//
//   • $msalReady consommé via useNuxtApp() (fourni par plugins/msal.client.ts)
//   • Scopes lus depuis useRuntimeConfig().public.msalApiScope
//   • handleRedirectPromise() appelé dans pages/login.vue → result passé
//     directement à handleRedirectCallback(result)
//   • Guards import.meta.client partout pour éviter tout crash SSR

import type { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser'
import type { AxiosResponse } from 'axios'
import axios from 'axios'
import type { ComputedRef, Ref } from 'vue'
import { ref, computed } from 'vue'
import { useUserStore } from '~/stores/user'
import type { UseAuthReturn } from '~/types/msal'
import type {
  OnboardingRequest,
  ProfileEnsureConflict,
  UserProfileDto,
} from '~/types/AccountProfile'
import { isAuthRequiredPath } from '~/utils/postLogoutRedirect'
import {
  clearMsalOAuthPending,
  consumeSignupPending,
  setEmailAlreadyRegisteredNotice,
  setMsalOAuthPending,
  setOnboardingPending,
} from '~/utils/msalOAuthSession'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import {
  enrichPostHogProfil,
  markSignupTrackedForUser,
  trackSignupCompletedOnOAuthCallback,
} from '~/composables/useTracking'
import { useFrontApi } from '~/composables/useFrontApi'
import { toMsalIdTokenClaims } from '~/utils/msalUtils'
import type { InterestEntry } from '~/types/Interest'
import { TempUserSession } from '~/utils/tempUserSession'
import { authCookies } from '~/utils/authCookies'

const EMAIL_ALREADY_REGISTERED_CODE: string = 'email_already_registered'

/**
 * Données d'un lead collectées dans le tunnel SEA pour créer un user temporaire (étape 4).
 */
export interface ProvisionTempUserPayload {
  email: string
  phone: string
  firstName: string
  lastName: string
  cities: string[]
  interests: InterestEntry[]
  landing: string
  schoolLevel: string
  originCtaUrl: string
}

/**
 * Auth MSAL (Entra CIAM) : helpers de login/logout + acquisition de token API.
 * @returns {UseAuthReturn} Fonctions et états réactifs d'authentification.
 */
export function useAuth(): UseAuthReturn {
  const userStore = useUserStore()
  const { identify, reset: resetTracking, track } = useTracking()
  const isLoading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  // ── Helpers internes ───────────────────────────────────────────────────────

  /**
   * Retourne l'instance MSAL injectée par le plugin Nuxt.
   * @returns {PublicClientApplication} Instance MSAL prête à l'emploi.
   * @throws {Error} Si le plugin MSAL n'a pas fourni d'instance (hors client ou init incomplète).
   */
  async function _getMsal(): Promise<PublicClientApplication> {
    const msal = await useNuxtApp().$msalReady
    if (!msal) {
      throw new Error('[useAuth] MSAL non initialisé ($msalReady indisponible)')
    }
    return msal
  }

  /**
   * Construit la liste des scopes à partir du runtime config.
   * @returns {string[]} Scopes MSAL.
   */
  function _getScopes(): string[] {
    const config = useRuntimeConfig()
    const raw: string | undefined = config.public.msalApiScope
    if (typeof raw === 'string' && raw.trim() !== '') {
      return raw.split(' ').filter(Boolean)
    }
    return ['openid', 'profile', 'email']
  }

  /**
   * Normalise les erreurs MSAL pour un affichage UI.
   * @param {unknown} e - Erreur interceptée.
   * @returns {string | null} Message utilisateur ou `null` si annulé par l'utilisateur.
   */
  function _parseError(e: unknown): string | null {
    if (e !== null && typeof e === 'object' && 'errorCode' in e) {
      const code = (e as { errorCode?: string }).errorCode
      if (code === 'user_cancelled') return null
      const message = (e as { message?: string }).message
      if (typeof message === 'string' && message !== '') return message
      return 'Une erreur est survenue'
    }
    if (e instanceof Error) {
      return e.message
    }
    return 'Une erreur est survenue'
  }

  /**
   * Crée le profil PrePeers du compte s'il n'existe pas encore, avant tout autre appel API.
   * @param {AuthenticationResult} result - Résultat MSAL de la connexion.
   * @returns {Promise<boolean>} `false` si l'email appartient déjà à un autre compte PrePeers : la connexion est refusée et la page part vers la déconnexion Entra.
   */
  async function _ensureProfile(result: AuthenticationResult): Promise<boolean> {
    try {
      const { data: profile }: AxiosResponse<UserProfileDto> = await useFrontApi().post(
        '/api/Profile/me/ensure',
        null,
        { headers: { Authorization: `Bearer ${result.accessToken}` } },
      )
      const hasAnsweredTunnelQuestions: boolean = Boolean(profile.seaLandingPageAlias)
      if (!hasAnsweredTunnelQuestions) setOnboardingPending()

      return true
    } catch (e: unknown) {
      const conflict: ProfileEnsureConflict | undefined =
        axios.isAxiosError<ProfileEnsureConflict>(e) && e.response?.status === 409
          ? e.response.data
          : undefined
      if (conflict?.code !== EMAIL_ALREADY_REGISTERED_CODE) {
        console.error('[useAuth] ensureProfile error:', e instanceof Error ? e.message : e)
        return true
      }

      setEmailAlreadyRegisteredNotice(conflict.email ?? null)
      clearMsalOAuthPending()
      userStore.logout()
      authCookies.clearAll()
      resetTracking()

      // Ferme aussi la session Entra : sinon Google reconnecte ce compte tout seul à la prochaine tentative.
      const msal: PublicClientApplication = await _getMsal()
      await msal.logoutRedirect({
        account: result.account,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      })
      return false
    }
  }

  /**
   * Hydrate le store utilisateur à partir d'un résultat MSAL.
   * @param {AuthenticationResult} result - Résultat MSAL.
   * @returns {Promise<boolean>} `false` si la connexion est refusée (email déjà inscrit).
   */
  async function _handleAuthResult(result: AuthenticationResult): Promise<boolean> {
    const isLoginAccepted: boolean = await _ensureProfile(result)
    if (!isLoginAccepted) return false

    const msal: PublicClientApplication = await _getMsal()
    const claims = toMsalIdTokenClaims(result.idTokenClaims)
    msal.setActiveAccount(result.account)
    const userId: string = claims.oid ?? result.account.localAccountId
    const firstName: string = result.account.name?.split(' ')[0] ?? 'Étudiant'
    userStore.login({
      userId,
      firstName,
      avatar: null,
      mbti: claims.extension_mbti ?? null,
      profileCompletion: claims.extension_profileCompletion ?? 0,
      token: result.accessToken,
    })
    authCookies.setHasAccount(true)
    // Rattache l'historique anonyme (UTM inclus) à l'identité connue.
    const email: string | undefined = result.account.username?.includes('@')
      ? result.account.username
      : undefined
    identify(userId, {
      firstName,
      name: result.account.name ?? undefined,
      email,
      source: 'msal',
    })

    await trackSignupCompletedOnOAuthCallback(userId, claims, consumeSignupPending())

    void enrichPostHogProfil(userId)
    return true
  }

  // ── API publique ───────────────────────────────────────────────────────────

  /**
   * Connexion email — déclenche un loginRedirect vers Entra CIAM.
   * Le retour arrive sur /login (redirectUri) et est traité par handleRedirectCallback.
   * @param {string} email - Email de connexion.
   */
  async function loginWithEmail(email: string): Promise<void> {
    if (!import.meta.client) return
    isLoading.value = true
    error.value = null
    try {
      const msal: PublicClientApplication = await _getMsal()
      setMsalOAuthPending()
      await msal.loginRedirect({
        scopes: _getScopes(),
        loginHint: email,
        prompt: 'login',
      })
    } catch (e: unknown) {
      error.value = _parseError(e)
      isLoading.value = false
    }
  }

  /**
   * Provisionne un lead temporaire (tunnel SEA, étape 4) : crée le user via la route Nitro,
   * hydrate le store, persiste le JWT temp + l'indice téléphone, puis rattache le parcours analytics.
   * @param {ProvisionTempUserPayload} data - Données du lead collectées dans le tunnel.
   * @returns {Promise<void>}
   * @throws {Error} Propage l'erreur si la création échoue (ex. 409 e-mail déjà utilisé).
   */
  async function provisionTempUser(data: ProvisionTempUserPayload): Promise<void> {
    if (!import.meta.client) return
    isLoading.value = true
    error.value = null
    try {
      const { data: result } = await useFrontApi().post<{
        userId: string
        token: string
        expiresIn: number
      }>('/api/Identity/registertemporaryuser', data)

      // Hydrate le store comme pour un user MSAL
      userStore.login({
        userId: result.userId,
        firstName: data.firstName,
        avatar: null,
        mbti: null,
        profileCompletion: 0,
        token: result.token,
      })
      userStore.updateProfile({
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
      })
      authCookies.setTempToken(result.token)
      authCookies.setHasPhone(data.phone.trim() !== '')
      // Rattache le parcours anonyme (UTM de la campagne SEA) au lead créé.
      identify(result.userId, {
        email: data.email,
        name: `${data.firstName} ${data.lastName}`.trim(),
        firstName: data.firstName,
        phone: data.phone,
        source: 'sea_tunnel',
      })
      track(TRACKING_EVENTS.SIGNUP_COMPLETED, { method: 'sea_tunnel' })
      markSignupTrackedForUser(result.userId)
      void enrichPostHogProfil(result.userId)
    } catch (e: unknown) {
      error.value = _parseError(e)
      throw e
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Met à jour le téléphone du profil connecté (tunnel SEA, étape 5).
   * @param {string} phoneE164 - Numéro complet (format E.164).
   * @returns {Promise<void>}
   */
  async function updateProfilePhone(phoneE164: string): Promise<void> {
    if (!import.meta.client) return
    const trimmed: string = phoneE164.trim()
    if (trimmed === '') return

    await useFrontApi().post('/api/Profile/updatephone', JSON.stringify(trimmed), {
      headers: { 'Content-Type': 'application/json' },
    })
    userStore.updateProfile({ phone: trimmed })
    authCookies.setHasPhone(true)
  }

  /**
   * Enregistre l'accord pour être appelé par les écoles (case de l'étape téléphone).
   * @param {boolean} hasAccepted - `true` si la case est cochée.
   * @returns {Promise<void>}
   * @throws {Error} Si l'enregistrement échoue.
   */
  async function saveSchoolPhoneContactConsent(hasAccepted: boolean): Promise<void> {
    if (!import.meta.client) return

    await useFrontApi().post(
      '/api/Profile/school-phone-contact-consent',
      JSON.stringify(hasAccepted),
      { headers: { 'Content-Type': 'application/json' } },
    )
  }

  /**
   * Enregistre les réponses de l'onboarding (niveau, villes, intérêts) sur le compte connecté.
   * @param {OnboardingRequest} answers - Niveau, villes, intérêts, alias de landing et URL du CTA d'origine.
   * @returns {Promise<void>}
   * @throws {Error} Si l'enregistrement échoue.
   */
  async function saveOnboardingAnswers(answers: OnboardingRequest): Promise<void> {
    await useFrontApi().post('/api/Profile/onboarding', answers)
  }

  /**
   * Connexion sociale (Google / Facebook).
   * @param {string} provider - Domaine provider (ex: 'google.com', 'facebook.com').
   */
  async function loginWithSocial(provider: string): Promise<void> {
    if (!import.meta.client) return
    isLoading.value = true
    error.value = null
    try {
      const msal: PublicClientApplication = await _getMsal()
      setMsalOAuthPending()
      const loginCallbackUrl: string = window.location.origin + '/login'
      await msal.loginRedirect({
        scopes: _getScopes(),
        prompt: 'select_account',
        redirectUri: loginCallbackUrl,
        redirectStartPage: loginCallbackUrl,
        extraQueryParameters: { domain_hint: provider },
      })
    } catch (e: unknown) {
      error.value = _parseError(e)
      isLoading.value = false
    }
  }

  /**
   * Appelé dans login.vue (onMounted) avec le result de handleRedirectPromise().
   * Hydrate le store utilisateur.
   * @param {AuthenticationResult} result - Résultat MSAL.
   * @returns {Promise<boolean>} `false` si la connexion est refusée et que la page part vers la déconnexion Entra.
   */
  async function handleRedirectCallback(result: AuthenticationResult): Promise<boolean> {
    if (!import.meta.client) return true
    try {
      return await _handleAuthResult(result)
    } catch (e: unknown) {
      error.value = _parseError(e)
      console.error('[useAuth] handleRedirectCallback error:', e)
      return true
    }
  }

  /**
   * Acquiert un access token silencieusement pour les appels API.
   * Ne déclenche pas `acquireTokenRedirect` : pendant le flux post-login, cela renvoyait
   * vers Entra puis `/login` alors que la session Pinia était déjà hydratée.
   * @returns {Promise<string | null>} Token d'accès ou `null` si indisponible.
   */
  async function getApiToken(): Promise<string | null> {
    if (!import.meta.client) return null

    TempUserSession.restoreTokenToStore()

    // Si on a déjà un token en store (MSAL ou temp), on le renvoie
    if (userStore.token) {
      return userStore.token
    }

    // Pas de compte connu → on coupe court avant l'init MSAL (différée ~5s sur pages publiques).
    if (!authCookies.hasAccount()) return null

    // Sinon, on tente l'acquisition silencieuse MSAL (cas user Entra)
    const msal: PublicClientApplication = await _getMsal()
    const account = msal.getActiveAccount()
    if (!account) return null

    const trySilent = async (forceRefresh: boolean): Promise<string | null> => {
      try {
        const result: AuthenticationResult = await msal.acquireTokenSilent({
          scopes: _getScopes(),
          account,
          forceRefresh,
        })
        userStore.setAccessToken(result.accessToken)
        return result.accessToken
      } catch {
        return null
      }
    }

    const first: string | null = await trySilent(false)
    if (first) return first
    return await trySilent(true)
  }

  /**
   * Déconnexion — temp user ou page publique : nettoyage local puis home.
   * Page privée MSAL : `logoutRedirect` vers la home via Entra.
   * @returns {Promise<void>} Résolution après logout local ou lancement du redirect MSAL.
   */
  async function logout(): Promise<void> {
    if (!import.meta.client) return
    const route = useRoute()
    const fullPath: string = route.fullPath
    const msal: PublicClientApplication = await _getMsal()
    const account = msal.getActiveAccount() ?? undefined

    userStore.logout()
    authCookies.clearAll()
    // Repart d'un visiteur anonyme côté analytics (pas de fuite d'identité entre sessions).
    resetTracking()

    // Temp user : pas de compte MSAL → home immédiate.
    if (!account) {
      await navigateTo('/')
      return
    }

    if (!isAuthRequiredPath(fullPath)) {
      await msal.clearCache({ account })
      msal.setActiveAccount(null)
      await navigateTo('/')
      return
    }

    await msal.logoutRedirect({
      account,
      postLogoutRedirectUri: `${window.location.origin}/`,
    })
  }

  const isLoggedIn: ComputedRef<boolean> = computed(() => userStore.isLoggedIn)

  return {
    isLoading,
    error,
    isLoggedIn,
    loginWithEmail,
    loginWithSocial,
    provisionTempUser,
    updateProfilePhone,
    saveSchoolPhoneContactConsent,
    saveOnboardingAnswers,
    handleRedirectCallback,
    getApiToken,
    logout,
  }
}
